import React, { useEffect, useState } from "react";
import BatchListing from "./BatchListing";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/redux/store";
import {
  setIsTableLoading,
  setPage,
  setRowsPerPage,
  setSearchValue,
  setItems,
  setTotalItems,
} from "src/redux/slices/BatchSlice";
import {
  useDeleteBatchMutation,
  useGetBatchesQuery,
} from "src/services/BatchServices";
import { RxPencil1 } from "react-icons/rx";
import { MdDeleteOutline } from "react-icons/md";
import { showConfirmationDialog } from "src/utils/showConfirmationDialog";
import { showToast } from "src/utils/showToaster";
import EditBatchFormWrapper from "../Edit/EditBatchFormWrapper";
import ATMMenu from "src/components/UI/atoms/ATMMenu/ATMMenu";
import {
  UserModuleActionTypes,
  UserModuleNameTypes,
} from "src/models/userAccess/UserAccess.model";
import { isCheckColumnPermission } from "src/helpers/userAccessFun";

type Props = {};

const BatchListingWrapper = (props: Props) => {
  const [batchId, setBatchId] = useState("");
  const [isOpenEditBatchDialog, setIsOpenEditBatchDialog] = useState(false);

  const dispatch = useDispatch();
  const { items, totalItems, page, rowsPerPage, searchValue, isTableLoading } =
    useSelector((state: RootState) => state.batch);

  const { data, isLoading, isFetching } = useGetBatchesQuery({
    limit: rowsPerPage,
    searchValue: searchValue,
    params: [
      "batchName",
      "batchCourses",
      "modeId",
      "branchId",
      "allBranches",
      "startDate",
      "estimatedEndDate",
      "time",
      "trainers",
      "language",
      "fees",
      "noOfSeats",
    ],
    page: page,
    filterBy: [],
    dateFilter: {},
    orderBy: "createdAt",
    orderByValue: -1,
    isPaginationRequired: true,
  });

  const [deleteBatch] = useDeleteBatchMutation();

  useEffect(() => {
    if (isLoading || isFetching) {
      dispatch(setIsTableLoading(true));
    } else {
      dispatch(setItems(data?.data || []));
      dispatch(setTotalItems(data?.totalItem || 0));
      dispatch(setIsTableLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, isFetching]);

  const getActionOptions = (batchId: string) => {
    return [
      {
        label: (
          <div className="flex gap-2 items-center text-secondary-main">
            <RxPencil1 className="text-lg" /> Edit
          </div>
        ),
        onClick: () => {
          setBatchId(batchId);
          setIsOpenEditBatchDialog(true);
        },
        actionName: UserModuleActionTypes.EDIT,
        moduleName: UserModuleNameTypes.BATCHES,
      },
      {
        label: (
          <div className="flex gap-2 items-center text-red-600 font-semibold">
            <MdDeleteOutline className="text-lg" /> Delete
          </div>
        ),
        onClick: () => {
          showConfirmationDialog({
            title: "Heads Up",
            text: "Are you sure, you want to Delete this Batch ?",
            icon: "question",
            showCancelButton: true,
            next: (result) => {
              if (result.isConfirmed) {
                deleteBatch(batchId).then((res: any) => {
                  if (res?.error) {
                    showToast("error", res?.error?.data?.message);
                  } else {
                    if (res?.data?.status) {
                      showToast("success", res?.data?.message);
                    } else {
                      showToast("error", res?.data?.message);
                    }
                  }
                });
              }
            },
          });
        },
        actionName: UserModuleActionTypes.DELETE,
        moduleName: UserModuleNameTypes.BATCHES,
      },
    ];
  };


  const columns: columnTypes[] = [
    {
      field: "batchName",
      headerName: "Batch Name",
      flex: "flex-[1_1_0%]",
      extraClasses: "capitalize",
    },
    {
      field: "courseName",
      headerName: "Course",
      flex: "flex-[1_1_0%]",
      extraClasses: "capitalize",
    },
    {
      field: "modeName",
      headerName: "Mode",
      flex: "flex-[1_1_0%]",
      extraClasses: "capitalize",
    },
    {
      field: "startDate",
      headerName: "Starts From",
      flex: "flex-[1_1_0%]",
      extraClasses: "",
    },
    {
      field: "time",
      headerName: "Timings",
      flex: "flex-[1_1_0%]",
      extraClasses: "",
    },

    {
      field: "languageName",
      headerName: "Language",
      flex: "flex-[1_1_0%]",
      extraClasses: "capitalize",
    },
    {
      field: "fees",
      headerName: "Fees",
      flex: "flex-[1_1_0%]",
      extraClasses: "",
      renderCell: (row: any) => <div> &#8377; {row?.fees} </div>,
    },
    {
      field: "noOfSeats",
      headerName: "Seats",
      flex: "flex-[1_1_0%]",
      extraClasses: "",
    },
    {
      field: "plans",
      headerName: "plans",
      flex: "flex-[1_1_0%]",
      extraClasses: "",
      renderCell: (row: any) => <div>{row?.plans?.map((el:any)=>el?.planName  )} </div>,
    },
    {
      field: "action",
      headerName: "Actions",
      flex: "flex-[1_1_0%]",
      extraClasses: "",
      renderCell: (row) => {
        const options = getActionOptions(row?._id);
        return <ATMMenu options={options} orientation="vertical" />;
      },
    },
  ];

  return (
    <div>
      <BatchListing
        columns={isCheckColumnPermission({
          moduleName: UserModuleNameTypes.BATCHES,
          actionName: UserModuleActionTypes.LIST,
          columns: columns
        })}
        rows={items}
        paginationProps={{
          isTableLoading,
          totalItems,
          page,
          rowsPerPage,
          searchValue,
          setPage: (newPage) => dispatch(setPage(newPage)),
          setRowsPerPage: (newRowsPerPage) =>
            dispatch(setRowsPerPage(newRowsPerPage)),
          setSearchValue: (newValue) => dispatch(setSearchValue(newValue)),
        }}
      />

      {isOpenEditBatchDialog ? (
        <EditBatchFormWrapper
          batchId={batchId}
          onClose={() => setIsOpenEditBatchDialog(false)}
        />
      ) : null}
    </div>
  );
};

export default BatchListingWrapper;
